import { Dispatch, ReactNode, SetStateAction, useEffect, useState } from 'react'
import { FieldErrors, FieldValue, FieldValues } from 'react-hook-form'

import { isAssociatedArray } from '@/common/util/array'
import { LocalStorage, loadStorage, removeStorage, saveStorage } from '@/common/util/storage'

import { MessageType } from '@/components/view/molecules/bar/MessageBar'

export const NO_DELETABLE_MODEL_MESSAGE = '関連するデータがあるため、削除できません。'

export interface MessageBarState {
  type: MessageType
  message: string | ReactNode
  linkId?: string
  linkText?: string
}

export type NameLabelMapping = Record<string, string>

export const useMessageStates = (): [
  messages: MessageBarState[],
  setMessages: Dispatch<SetStateAction<MessageBarState[]>>,
  addMessage: (message: MessageBarState) => void,
  saveMessage: (messages: MessageBarState[]) => void
] => {
  const [messages, setMessages] = useState<MessageBarState[]>([])

  const addMessage = (message: MessageBarState) => {
    setMessages((messages) => {
      return [message, ...messages]
    })
  }

  const saveMessage = (messages: MessageBarState[]) => {
    saveStorage(LocalStorage.MESSAGES, JSON.stringify(messages))
  }

  useEffect(() => {
    const messageJson = loadStorage(LocalStorage.MESSAGES)
    if (messageJson) {
      setMessages(JSON.parse(messageJson))
      removeStorage(LocalStorage.MESSAGES)
    }
  }, [])

  return [messages, setMessages, addMessage, saveMessage]
}

export const convertToErrorMessageBarState = (
  message: string,
  name?: string,
  key?: string
): MessageBarState => {
  return {
    type: MessageType.CRITICAL,
    message: (name ? `${name}： ` : '') + message,
    linkId: key,
    linkText: name ? '該当場所へ移動' : undefined
  }
}

/**
 * Define scrolled link id
 * In object schema: Hyphen with object names (ex `normal-skuCode`
 * In array schema: Hyphen with index (ex `variationPriceStock-${index}-skuCode`
 */
const insertErrorMessages = <T extends FieldValues>(
  errorMessages: MessageBarState[],
  errors: FieldErrors<T>,
  nameLabelMapping?: NameLabelMapping,
  keyPrefix: string = '',
  index?: number
) => {
  const indexPrefix = index != undefined ? index + '-' : ''
  Object.keys(errors).forEach((key) => {
    if (errors[key as FieldValue<T>] && errors[key as FieldValue<T>].message) {
      const name =
        nameLabelMapping && nameLabelMapping[keyPrefix + indexPrefix + key]
          ? nameLabelMapping[keyPrefix + indexPrefix + key]
          : nameLabelMapping && nameLabelMapping[key]
            ? nameLabelMapping[key]
            : ''

      errorMessages.push(
        convertToErrorMessageBarState(
          errors[key as FieldValue<T>].message,
          name,
          keyPrefix + indexPrefix + key
        )
      )
    } else if (isAssociatedArray(errors[key as FieldValue<T>])) {
      insertErrorMessages(
        errorMessages,
        errors[key as FieldValue<T>],
        nameLabelMapping,
        keyPrefix + indexPrefix + key + '-'
      )
    } else if (Array.isArray(errors[key as FieldValue<T>])) {
      errors[key as FieldValue<T>].forEach((loopErrors: FieldErrors<T>, index: number) => {
        insertErrorMessages(
          errorMessages,
          loopErrors,
          nameLabelMapping,
          keyPrefix + indexPrefix + key + '-',
          index
        )
      })
    }
  })

  return errorMessages
}

export const useFormErrorMessages = <T extends FieldValues>(
  errors: FieldErrors<T>,
  setMessages: Dispatch<SetStateAction<MessageBarState[]>>,
  nameLabelMapping?: NameLabelMapping
) => {
  useEffect(() => {
    if (errors) {
      const errorMessages: MessageBarState[] = insertErrorMessages([], errors, nameLabelMapping)
      setMessages((messages) => {
        const existingMessages = messages.filter(
          (messageState) => messageState.type != MessageType.CRITICAL
        )
        return [...existingMessages, ...errorMessages]
      })
      if (errorMessages.length > 0) {
        window.scrollTo({
          top: 0,
          behavior: 'smooth'
        })
      }
    }
  }, [errors])
}
